import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, Save, AlertCircle } from "lucide-react";

/**
 * SurveyDetail V3
 * -----------------------------------------------------------
 * - Multi-page navigation (groups questions into pages defined on the survey)
 * - Conditional visibility ("visible_if") evaluated per render
 * - Draft auto-save (debounced) + draft restore on load (if survey.allow_draft_save)
 * - Per-page Next/Prev with required-field validation
 * - Progress bar and "page X of Y" indicator
 */

function evalVisibility(rule, answers) {
  if (!rule) return true;
  // Format: "qid==value" or "qid=value" or "qid!=value"
  const m = rule.match(/^(\S+?)\s*(==|!=|=)\s*(.+)$/);
  if (!m) return true;
  const [, qid, op, valRaw] = m;
  const val = valRaw.trim().replace(/^["']|["']$/g, "");
  const a = answers[qid];
  if (Array.isArray(a)) return op === "!=" ? !a.includes(val) : a.includes(val);
  if (op === "!=") return String(a) !== val;
  return String(a) === val;
}

function buildPages(survey) {
  // If survey.pages defined → use it. Each page: { title, question_ids: [..] }
  if (survey?.pages?.length) {
    return survey.pages.map((p, idx) => ({
      title: p.title || `Page ${idx + 1}`,
      questions: (p.question_ids || []).map((qid) => survey.questions.find((q) => (q.id || "") === qid)).filter(Boolean),
    }));
  }
  // Fallback: 1 page with all questions
  return [{ title: "", questions: survey?.questions || [] }];
}

const SESSION_KEY = (id) => `khojbyte.survey.session.${id}`;

export default function SurveyDetail() {
  const { id } = useParams();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [pageIdx, setPageIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [done, setDone] = useState(false);
  const [validationErr, setValidationErr] = useState("");
  const startedAt = useRef(Date.now());
  const sessionId = useRef(null);

  // stable session id per browser per survey
  if (!sessionId.current) {
    const existing = localStorage.getItem(SESSION_KEY(id));
    sessionId.current = existing || `sess_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    if (!existing) localStorage.setItem(SESSION_KEY(id), sessionId.current);
  }

  // load survey + restore draft
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/surveys/${id}`);
        if (cancelled) return;
        setSurvey(r.data);
        if (r.data.allow_draft_save !== false) {
          try {
            const d = await api.get(`/surveys/${id}/draft`, { params: { session_id: sessionId.current } });
            if (cancelled) return;
            if (d.data?.answers && Object.keys(d.data.answers).length > 0) {
              setAnswers(d.data.answers);
              setPageIdx(d.data.current_page || 0);
              toast.success("Draft restored");
            }
          } catch (_) {}
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [id]);

  const pages = survey ? buildPages(survey) : [];
  const currentPage = pages[pageIdx] || { questions: [] };
  const visibleQs = currentPage.questions.filter((q) => evalVisibility(q.visible_if, answers));

  // Debounced draft auto-save
  const draftTimer = useRef(null);
  useEffect(() => {
    if (!survey || survey.allow_draft_save === false) return;
    if (Object.keys(answers).length === 0) return;
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(async () => {
      setDraftSaving(true);
      try {
        await api.post(`/surveys/${id}/draft`, {
          session_id: sessionId.current,
          answers,
          current_page: pageIdx,
        });
        setDraftSavedAt(new Date());
      } catch (_) {} finally { setDraftSaving(false); }
    }, 1200);
    return () => clearTimeout(draftTimer.current);
  }, [answers, pageIdx, id, survey]);

  const isAnswered = (q, qid) => {
    const v = answers[qid];
    if (q.type === "checkbox") return Array.isArray(v) && v.length > 0;
    return v !== undefined && v !== "" && v !== null;
  };

  const validateCurrentPage = () => {
    for (const q of visibleQs) {
      const qid = q.id || "";
      if (q.required && !isAnswered(q, qid)) {
        return `"${q.text}" is required`;
      }
    }
    return "";
  };

  const next = () => {
    const err = validateCurrentPage();
    if (err) { setValidationErr(err); return; }
    setValidationErr("");
    setPageIdx((p) => Math.min(p + 1, pages.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => {
    setValidationErr("");
    setPageIdx((p) => Math.max(p - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const err = validateCurrentPage();
    if (err) { setValidationErr(err); return; }
    setSubmitting(true);
    try {
      const duration_seconds = Math.round((Date.now() - startedAt.current) / 1000);
      const r = await api.post(`/surveys/${id}/respond`, {
        answers, duration_seconds,
        session_id: sessionId.current,
        is_complete: true,
      });
      setDone(true);
      localStorage.removeItem(SESSION_KEY(id));
      if (r.data.flags && r.data.flags.length > 0) {
        toast.warning(`Response submitted (flags: ${r.data.flags.join(", ")})`);
      } else {
        toast.success("Response submitted — thank you!");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!survey) {
    return <div className="min-h-screen flex items-center justify-center bg-kb-bg kb-mono text-xs text-kb-ink-muted">Loading…</div>;
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-kb-bg px-6" data-testid="survey-done">
        <div className="text-center max-w-md">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" strokeWidth={1.5} />
          <h2 className="font-serif text-3xl text-kb-ink mt-4">Thank you</h2>
          <p className="text-kb-ink-muted mt-2">Your response has been recorded.</p>
        </div>
      </div>
    );
  }

  const isLastPage = pageIdx === pages.length - 1;
  const progressPct = pages.length > 1 ? Math.round(((pageIdx + 1) / pages.length) * 100) : 100;

  return (
    <div className="min-h-screen bg-kb-bg py-12 px-6" data-testid="survey-respond-page">
      <div className="max-w-2xl mx-auto">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SURVEY · {survey.id} · {survey.language?.toUpperCase()}</div>
        <h1 className="font-serif text-3xl text-kb-ink mt-2 leading-tight">{survey.title}</h1>
        {survey.description && <p className="text-kb-ink-muted mt-3">{survey.description}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {survey.anonymous && (
            <span className="inline-flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-1.5">
              Anonymous · your identity is not stored
            </span>
          )}
          {survey.allow_draft_save !== false && (
            <span className="inline-flex items-center gap-2 text-xs text-kb-primary bg-kb-primary/5 border border-kb-primary/15 rounded-sm px-3 py-1.5">
              <Save className="w-3 h-3" /> Draft auto-saves
            </span>
          )}
        </div>

        {/* Progress bar (only when multi-page) */}
        {pages.length > 1 && (
          <div className="mt-6">
            <div className="flex items-center justify-between text-[11px] kb-mono uppercase tracking-wider text-kb-ink-muted">
              <span data-testid="survey-page-indicator">Page {pageIdx + 1} of {pages.length}{currentPage.title ? ` · ${currentPage.title}` : ""}</span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-1 bg-slate-200 rounded-full mt-1.5 overflow-hidden">
              <div className="h-1 bg-kb-primary transition-all duration-300" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 kb-card p-7 space-y-6" data-testid="survey-respond-form">
          {visibleQs.length === 0 ? (
            <div className="text-sm text-kb-ink-muted italic">No questions visible on this page based on prior answers.</div>
          ) : (
            visibleQs.map((q, idx) => {
              const qid = q.id || `q${idx + 1}`;
              return (
                <div key={qid} data-testid={`survey-q-${qid}`}>
                  <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Q{idx + 1} · {q.type}</label>
                  <div className="text-kb-ink mt-1 mb-3 font-medium">{q.text}{q.required && <span className="text-rose-500 ml-1">*</span>}</div>
                  {q.type === "text" && (
                    <input className="kb-input" value={answers[qid] || ""}
                      onChange={(e) => setAnswers({ ...answers, [qid]: e.target.value })}
                      data-testid={`respond-q-${qid}`} />
                  )}
                  {q.type === "long_text" && (
                    <textarea rows={4} className="kb-input resize-none" value={answers[qid] || ""}
                      onChange={(e) => setAnswers({ ...answers, [qid]: e.target.value })}
                      data-testid={`respond-q-${qid}`} />
                  )}
                  {q.type === "number" && (
                    <input type="number" className="kb-input" value={answers[qid] || ""}
                      onChange={(e) => setAnswers({ ...answers, [qid]: e.target.value })}
                      data-testid={`respond-q-${qid}`} />
                  )}
                  {q.type === "date" && (
                    <input type="date" className="kb-input" value={answers[qid] || ""}
                      onChange={(e) => setAnswers({ ...answers, [qid]: e.target.value })}
                      data-testid={`respond-q-${qid}`} />
                  )}
                  {(q.type === "rating" || q.type === "likert") && (
                    <div className="flex gap-2 flex-wrap">
                      {(q.type === "likert" ? [1, 2, 3, 4, 5, 6, 7] : [1, 2, 3, 4, 5]).map((n) => (
                        <button type="button" key={n}
                          onClick={() => setAnswers({ ...answers, [qid]: n })}
                          className={`w-10 h-10 rounded-sm border kb-mono text-sm ${answers[qid] === n ? "border-kb-primary bg-kb-primary text-white" : "border-slate-300 bg-white text-kb-ink"}`}
                          data-testid={`respond-q-${qid}-rating-${n}`}>{n}</button>
                      ))}
                    </div>
                  )}
                  {q.type === "multiple_choice" && (
                    <div className="space-y-2">
                      {(q.options || "").split(",").map((opt) => opt.trim()).filter(Boolean).map((opt) => (
                        <label key={opt} className="flex items-center gap-3 border border-slate-200 rounded-sm px-3 py-2 cursor-pointer hover:border-kb-primary">
                          <input type="radio" name={`q-${qid}`} value={opt}
                            checked={answers[qid] === opt}
                            onChange={(e) => setAnswers({ ...answers, [qid]: e.target.value })}
                            data-testid={`respond-q-${qid}-opt-${opt}`} />
                          <span className="text-sm text-kb-ink">{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {q.type === "checkbox" && (
                    <div className="space-y-2">
                      {(q.options || "").split(",").map((opt) => opt.trim()).filter(Boolean).map((opt) => {
                        const current = answers[qid] || [];
                        const checked = current.includes(opt);
                        return (
                          <label key={opt} className="flex items-center gap-3 border border-slate-200 rounded-sm px-3 py-2 cursor-pointer hover:border-kb-primary">
                            <input type="checkbox" checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked ? [...current, opt] : current.filter((x) => x !== opt);
                                setAnswers({ ...answers, [qid]: next });
                              }}
                              data-testid={`respond-q-${qid}-chk-${opt}`} />
                            <span className="text-sm text-kb-ink">{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {validationErr && (
            <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2 text-sm text-rose-700" data-testid="survey-validation-err">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{validationErr}</span>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <button type="button" onClick={prev} disabled={pageIdx === 0}
              className="kb-btn-outline text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="survey-prev-btn">
              <ChevronLeft className="w-3.5 h-3.5" /> Previous
            </button>
            <div className="text-[11px] kb-mono uppercase tracking-wider text-kb-ink-light">
              {draftSaving ? "Saving…" : draftSavedAt ? `Draft saved · ${draftSavedAt.toLocaleTimeString()}` : ""}
            </div>
            {isLastPage ? (
              <button type="submit" disabled={submitting}
                className="kb-btn-primary inline-flex items-center gap-1.5"
                data-testid="survey-respond-submit">
                {submitting ? "Submitting…" : "Submit"}
              </button>
            ) : (
              <button type="button" onClick={next} className="kb-btn-primary inline-flex items-center gap-1.5" data-testid="survey-next-btn">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
